import { LinkGuideLayout } from '../components/LinkGuideLayout';

const sections = [
  ['prepare', 'Prepare your computer and network'],
  ['account', 'Sign in and stay connected to Paddiq'],
  ['first-logger', 'Add your first MyChron'],
  ['with-link', 'Use a saved MyChron with Link'],
  ['mesh-and-router', 'Mesh, guest Wi-Fi and a different router'],
  ['hotspot', 'Return to standalone hotspot mode'],
  ['fleet', 'Work with several MyChrons'],
  ['circuits', 'Choose the circuit for each paddock and date'],
  ['race-studio-copies', 'Save session files to a folder'],
  ['race-studio-transfers', 'Review Race Studio transfers'],
  ['lan', 'Find MyChrons already on the LAN'],
  ['future', 'Choose future sessions for each logger'],
  ['updates', 'Update Link without losing saved work'],
  ['troubleshoot', 'Check connection and session status'],
] as const;

export function PaddiqLinkGuidePage() {
  return (
    <LinkGuideLayout
      path="/resources/paddiq-link"
      title="Connect MyChrons with Paddiq Link"
      lead="Install Link, sign in and connect your first MyChron. Then choose sessions, manage file copies or follow the recovery guide."
      sections={sections}
    >
      <section className="guide-step" id="prepare">
        <div>
          <h2>Prepare your computer and network.</h2>
          <p>
            The download page shows the installer that matches your computer when it can identify
            it. On a Mac, choose Apple silicon or Intel using Apple menu &gt; About This Mac if
            prompted. Expand <strong>Other downloads</strong> to choose a different installer.
            Paddiq Link runs on a Mac or Windows computer, not a phone or tablet.
          </p>
          <p>
            Sign in to the correct Paddiq workspace. Keep the selected MyChron powered on and close
            other software, including Race Studio, that is using its connection.
          </p>
          <ol className="guide-instructions">
            <li>
              Before provisioning, the <strong>computer running Link</strong> must join the selected
              MyChron’s own Wi-Fi hotspot. Read the exact hotspot name on the logger and select it
              in the computer’s Wi-Fi settings.
            </li>
            <li>
              Stay on that hotspot if the computer reports no internet. Probing the logger is a
              local operation.
            </li>
            <li>
              After applying paddock settings, reconnect the computer to the{' '}
              <strong>same paddock network</strong> as the MyChrons. An unrelated phone hotspot or
              other Wi-Fi prevents local communication.
            </li>
          </ol>
          <p>
            Use a password-protected WPA2-Personal network that allows local devices to communicate.
            WPA3-only networks and guest/client isolation can prevent connection. Enter the current
            paddock password; Link cannot recover a forgotten password. If the OS hides the Wi-Fi
            name, check it manually rather than assuming Link detected it.
          </p>
        </div>
      </section>
      <section className="guide-step" id="account">
        <div>
          <h2>Sign in and stay connected to Paddiq.</h2>
          <p>
            Sign in with your Paddiq account and confirm the workspace that should receive your
            sessions. Paddiq Link and Paddiq Link Dev use separate accounts, environments and local
            data. Use the app supplied for your environment. Sign-in fields, labels and the password
            visibility control support light and dark mode; interface settings are at the bottom of
            the sidebar after sign-in.
          </p>
          <p>
            Updated Link renews your sign-in automatically when the matching Paddiq service supports
            it. You should not need to sign in every time a short-lived access token expires. An
            expired sign-in from an older build needs one normal sign-in to start renewal. An
            expired or revoked session, a changed password, or changed account access can still
            require you to sign in again.
          </p>
          <p>
            If <strong>Token expired or invalid</strong> appears while loading a circuit,
            assignments or account data, check Link’s sign-in status and internet access. That
            message concerns access to Paddiq; it does not establish that your MyChron has left
            Wi-Fi. Follow any sign-in prompt and retry the affected action. Keep the saved MyChron
            and local queue.
          </p>
          <p>
            Internet is required to sign in, verify workspace choices and upload. A local logger
            connection and a cloud upload are separate steps. Signing out clears the local
            credentials; it does not transfer another account’s saved files or logger ownership to
            the next person who signs in.
          </p>
        </div>
      </section>
      <section className="guide-step" id="first-logger">
        <div>
          <h2>Add your first MyChron.</h2>
          <ol className="guide-instructions">
            <li>
              Choose <strong>Add MyChron</strong>. If the logger is already on the paddock network,
              use <strong>Already on Wi-Fi</strong> and follow the{' '}
              <a href="#lan">LAN discovery steps</a>.
            </li>
            <li>
              For hotspot setup, join the logger’s exact hotspot on this computer, then choose{' '}
              <strong>Check connection</strong>. Confirm the detected logger before continuing.
            </li>
            <li>
              Choose an authorized driver and optional kart. Select{' '}
              <strong>New sessions only</strong> to leave existing files on the logger, or{' '}
              <strong>Review existing sessions</strong> to inspect them first. Use{' '}
              <strong>Pull all existing sessions</strong> only when the entire backlog belongs in
              this workspace with the assignment you selected.
            </li>
            <li>
              Enter the paddock Wi-Fi details, review the setup, and keep Link and the logger
              powered on while settings are applied and verified. Join the paddock Wi-Fi on the
              computer when prompted.
            </li>
            <li>
              Check the completion summary and choose <strong>View sessions</strong>. Confirm the
              paddock circuit and sync settings before starting transfers.
            </li>
          </ol>
          <p>
            A successful probe reads the logger; it does not itself change Wi-Fi settings. Hardware
            setup controls depend on the installed build’s capabilities. Do not remove and re-add a
            saved logger to retry a connection.
          </p>
        </div>
      </section>
      <section className="guide-step" id="with-link">
        <div>
          <h2>Use a saved MyChron with Link.</h2>
          <p>
            If the MyChron should already be on its saved paddock Wi-Fi, keep both devices on that
            network and use <strong>Reconnect</strong> or{' '}
            <strong>Connection help → Retry connection</strong>. This checks the connection; it does
            not send new Wi-Fi settings. Use the steps below when deliberately moving a logger from
            hotspot mode onto paddock Wi-Fi.
          </p>
          <ol className="guide-instructions">
            <li>
              Open <strong>MyChrons</strong>, choose the existing logger by its name and stable
              identity. For a logger in hotspot mode, choose{' '}
              <strong>Connect to paddock Wi-Fi</strong> (called <strong>Use with Link</strong> in
              earlier builds).
            </li>
            <li>
              If the MyChron is already in hotspot mode, choose{' '}
              <strong>MyChron is in hotspot mode</strong>. Join its hotspot on this computer and
              verify the selected logger. You do not need to remove and add it again.
            </li>
            <li>
              If it is still on paddock Wi-Fi, connect the computer to that network and use{' '}
              <strong>Return to hotspot first</strong>, then verify it on its own hotspot.
            </li>
            <li>
              Enter the team’s paddock network and current password. Review Wi-Fi power, then choose{' '}
              <strong>Apply paddock settings</strong>.
            </li>
            <li>
              Let the safety checks and handoff finish. Link attempts to connect the computer to the
              paddock network and verifies the same logger and its settings before reporting
              success.
            </li>
          </ol>
          <p>
            Changing networks can interrupt access to other loggers. Link waits for active transfers
            before applying settings. Cancel before Apply to leave the logger unchanged. After a
            write starts, keep Link open until verification completes.
          </p>
          <p>
            If the handoff fails, read the logger’s display. For <strong>NWK JOINED</strong>,
            reconnect the computer to the named paddock network and choose{' '}
            <strong>Verify pending paddock connection</strong>. When adding a MyChron, choose{' '}
            <strong>Verify saved settings</strong> if offered. Verification retries the connection
            without sending the Wi-Fi settings again. For hotspot/AP, rejoin the logger’s hotspot,
            verify its identity again and retry with corrected settings. Keep the saved record and
            local recovery data.
          </p>
        </div>
      </section>
      <section className="guide-step" id="mesh-and-router">
        <div>
          <h2>Mesh, guest Wi-Fi and a different router.</h2>
          <p>
            A mesh can work when local devices can communicate. Guest isolation or separate network
            segments can block that connection even when both devices appear connected.
          </p>
          <p>
            <a href="/resources/paddiq-link-troubleshooting#mesh-and-router">
              Check mesh Wi-Fi and router changes
            </a>
            .
          </p>
        </div>
      </section>
      <section className="guide-step" id="hotspot">
        <div>
          <h2>Return to standalone hotspot mode.</h2>
          <p>
            You can also change AP or Existing Network settings in Race Studio’s Wi-Fi &amp;
            Properties panel, transmit them and let the logger reboot. Follow{' '}
            <a href="https://www.aim-sportline.com/download/doc/eng/mychron6/aim_mychron6_user_guide_eng.pdf">
              AiM’s MyChron6 manual, sections 4.5.1–4.5.2
            </a>
            . Close Race Studio before connecting Link again. Keep the existing saved device and
            verify its identity after changing the hotspot name or address.
          </p>
          <p>
            Open the same saved MyChron and choose <strong>Restore hotspot</strong> (called{' '}
            <strong>Use hotspot mode</strong> in earlier builds). While it is reachable on paddock
            Wi-Fi, Link can apply the guarded mode change, reconnect to its hotspot and verify it.
          </p>
          <p>
            Review the remembered network before confirming; Link restores its saved hotspot
            settings, including the password. If the logger is already in hotspot mode, use{' '}
            <strong>Already in hotspot mode</strong> to verify its current connection without
            replacing its password.
          </p>
          <p>
            If it is unreachable or this Link build cannot change hardware settings, follow the
            documented recovery procedure for your model or contact support. Link cannot send a mode
            change to a logger it cannot reach. Do not treat a configuration reset as a simple mode
            toggle: it can change saved settings. Once the logger is in hotspot mode, join it and
            follow the identity-verification steps. Automated settings changes require a build
            authorized for that workflow.
          </p>
          <p>
            The saved logger, assignments, session history and queued files remain. Hotspot mode is
            separate from powering off, <strong>Pause all</strong>, or removing the device.
            Reconnect the computer to paddock Wi-Fi to reach your other loggers. Return this logger
            later using <strong>Connect to paddock Wi-Fi</strong>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="fleet">
        <div>
          <h2>Work with several MyChrons.</h2>
          <p>
            Manage each MyChron’s driver, kart, sessions and sync switch. Pause all and the
            individual switches control automatic work; you can deliberately select historical
            recordings.
          </p>
          <p>
            <a href="/resources/paddiq-link-sessions#fleet">Manage MyChrons and syncing</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="circuits">
        <div>
          <h2>Choose the circuit for each paddock and date.</h2>
          <p>
            Choose the exact circuit and direction for the paddock and recording date. Existing
            queued or uploaded sessions are not silently reassigned.
          </p>
          <p>
            <a href="/resources/paddiq-link-sessions#circuits">Choose circuits and assignments</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="race-studio-copies">
        <div>
          <h2>Save session files to a folder.</h2>
          <p>
            Optionally save new downloaded originals to a folder. Copies have their own status and
            do not automatically register sessions in Race Studio.
          </p>
          <p>
            <a href="/resources/paddiq-link-file-copies#enable">Set up session file copies</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="race-studio-transfers">
        <div>
          <h2>Review Race Studio transfers.</h2>
          <p>
            On Windows, open Integrations → Race Studio to import XRZ/XRK sessions into Paddiq,
            export retained originals into Race Studio’s database, or enable automatic export for
            new MyChron downloads. Review driver and kart mappings; circuits are optional.
          </p>
          <p>
            <a href="/resources/paddiq-link-race-studio">Review Race Studio transfers</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="lan">
        <div>
          <h2>Find MyChrons already on the LAN.</h2>
          <p>
            In builds with <strong>MyChrons visible on this network</strong>, Link scans the
            computer’s current LAN in small batches, including when no loggers are saved yet. Leave
            the computer and loggers on the same network. Scanning waits while sessions are
            transferring; an empty result can also mean a logger is off, out of range, on another
            network or busy in Race Studio.
          </p>
          <p>
            A saved logger that was changed in Race Studio is matched by its protocol identity. A
            new IP address or Existing Network SSID does not create another logger or erase its
            assignments, history or queued files. Link reads the mode before reconnecting; a logger
            found in hotspot mode stays out of automatic syncing. If the OS hides the hotspot name,
            check it manually.
          </p>
          <p>
            A new visible logger is not automatically registered or authorized to upload. Choose{' '}
            <strong>Add this MyChron</strong>, check the identity on your own logger, enter its
            exact hotspot name, choose an authorized active driver and optional compatible kart, and
            confirm that it belongs to your team. Link rechecks that identity on the LAN before
            saving. Internet access is required to verify the team list. Existing sessions remain
            unselected under the new-session baseline; use the per-device history to review older
            sessions explicitly.
          </p>
          <p>
            Automatic discovery does not change Wi-Fi settings or create a secret recovery backup.
            Hardware mode changes still use the authorized setup/recovery workflow above. If these
            controls are absent, use the instructions for your installed version or contact support.
          </p>
        </div>
      </section>
      <section className="guide-step" id="future">
        <div>
          <h2>Choose future sessions for each logger.</h2>
          <p>
            Use Sync settings to choose a logger’s future driver, kart and timing. Today and From
            now also respect the latest resume or enable time; select earlier recordings manually.
          </p>
          <p>
            <a href="/resources/paddiq-link-sessions#future">Set up automatic sessions</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="updates">
        <div>
          <h2>Update Link without losing saved work.</h2>
          <p>
            Open Paddiq Link updates at the bottom of the sidebar. Updates can download
            automatically; keep Link open while it finishes active work before restarting.
          </p>
          <p>
            <a href="/resources/paddiq-link-troubleshooting#updates">Update Link safely</a>.
          </p>
        </div>
      </section>
      <section className="guide-step" id="troubleshoot">
        <div>
          <h2>Check connection and session status.</h2>
          <p>
            Connection checks, logger downloads and cloud uploads are separate stages. Keep saved
            loggers and local files while you identify which stage needs attention.
          </p>
          <p>
            <a href="/resources/paddiq-link-troubleshooting#status">
              Check connection and transfer status
            </a>
            .
          </p>
        </div>
      </section>
    </LinkGuideLayout>
  );
}
